/**
 * The roster slice of the wire — Task T2-E-web (Tier-2 ops, pillars-deepening
 * program), consuming `GET /shifts/roster`, `GET /shifts`, `GET/POST
 * /shift-assignments` and `DELETE /shift-assignments/{id}`, all already built and
 * tested against `openapi.yaml`'s `Attendance` tag (Task T2-E, commit 95618da). Hand-
 * written to mirror the contract, the same discipline `geo.ts`'s and `employees.ts`'s
 * own headers state: transcribe what the screen reads, not the whole document, and
 * this task does not touch `openapi.yaml`.
 *
 * ── `warnings` never blocks a create ─────────────────────────────────────────
 *
 * Five of `LabourCapWarning`'s six codes are SOFT — stored and returned, never a
 * refusal. `overlap` is the one HARD code, and it never actually reaches this wire
 * shape: naming it is refused with `400 WORKFORCE_SHIFT_OVERLAP` before anything is
 * written, so `ShiftAssignment.warnings` and `ShiftRosterDay.warnings` can only ever
 * carry the other five. `overlap` is still in `LABOUR_CAP_WARNING_CODES` below,
 * because the *type* is shared with the schema and a `Record` keyed on it must stay
 * total — but no screen renders a chip for it.
 *
 * ── Two different overlaps, two different HTTP statuses ──────────────────────
 *
 * `POST /shift-assignments` can refuse an overlap two ways, and a screen must handle
 * both as blocking:
 *
 *  - `409 WORKFORCE_SHIFT_ASSIGNMENT_OVERLAP` — two assignments at the **same rung**
 *    with overlapping effective ranges (`replace_existing: false`, the default).
 *  - `400 WORKFORCE_SHIFT_OVERLAP` — the labour-cap evaluator's own hard code, when
 *    the new shift's hours overlap an existing shift the employee already has.
 *
 * Both codes are in `api/errors.ts`'s `USER_SAFE_CODES` with a specific, accurate
 * default sentence (`src/lib/errors.ts`), so `ApiErrorNote` renders the server's own
 * message for either — no client-side re-wording needed for these two.
 */

import { qs, type QueryValue } from './paths';
import type { WorkforceApiError } from './errors';

/* ─────────────────────────────────────────────── shift catalogue (GET /shifts) ── */

export const SHIFTS_PATH = '/api/v1/shifts';

/**
 * `Shift` of `openapi.yaml`, narrowed to what the roster screen reads: the select
 * option's label and the historical-assignment lookup. `active` is not in the
 * schema's own `required` list, so it is optional here too rather than assumed present.
 */
export interface Shift {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly start_time: string;
  readonly end_time: string;
  readonly crosses_midnight: boolean;
  readonly active?: boolean;
}

export interface ShiftListResponse {
  readonly data: readonly Shift[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

/**
 * `listShifts`'s own description: "a small catalogue" — so this screen reads it once,
 * at the endpoint's own maximum (`OffsetLimit.maximum`), rather than building a second
 * pagination UI for a list a company keeps in the dozens.
 */
export const SHIFT_CATALOG_LIMIT = 200;

/** `active` narrows to the shifts a "Gán ca" select should offer; omit for every shift. */
export function shiftsPath(active?: boolean): string {
  return `${SHIFTS_PATH}${qs({ active, limit: SHIFT_CATALOG_LIMIT })}`;
}

/* ───────────────────────────────────────────────────── labour-cap warnings ────── */

/**
 * `LabourCapWarning.code`'s own enum, transcribed from `openapi.yaml` rather than
 * assumed — the brief's own instruction, because a sixth value invented from the name
 * alone would be exactly the kind of drift this file exists to avoid. `roster.test.ts`
 * cross-checks this array against the document itself.
 */
export const LABOUR_CAP_WARNING_CODES = [
  'daily_over_8h',
  'weekly_over_48h',
  'days_over_6_per_week',
  'rest_under_12h',
  'night_work',
  'overlap',
] as const;

export type LabourCapWarningCode = (typeof LABOUR_CAP_WARNING_CODES)[number];

/** `code` decides which field is populated: `days` for `days_over_6_per_week`, `minutes` for the rest. */
export interface LabourCapWarning {
  readonly code: LabourCapWarningCode;
  readonly detail: {
    readonly minutes?: number;
    readonly days?: number;
  };
}

/* ───────────────────────────────────────────────────────────── shift assignments ── */

export const SHIFT_SCOPE_TYPES = [
  'employee',
  'job_title',
  'department',
  'branch',
  'company',
] as const;
export type ShiftScopeType = (typeof SHIFT_SCOPE_TYPES)[number];

export const SHIFT_ASSIGNMENTS_PATH = '/api/v1/shift-assignments';

/** `ShiftAssignment` of `openapi.yaml`, every `required` member present. */
export interface ShiftAssignment {
  readonly id: string;
  readonly shift_id: string;
  readonly scope_type: ShiftScopeType;
  readonly scope_key: string;
  readonly priority: number;
  readonly employee_id: string | null;
  readonly effective_from: string;
  readonly effective_to?: string | null;
  readonly warnings: readonly LabourCapWarning[];
  readonly ended_by?: string | null;
  readonly ended_at?: string | null;
}

export interface ShiftAssignmentList {
  readonly assignments: readonly ShiftAssignment[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

/**
 * `GET /shift-assignments`'s own maximum page size. The drawer reads one employee's
 * rows (narrowed by `employee_id`) plus, per the endpoint's own documented shape, every
 * non-employee-scoped policy row in the company — a small set in practice, and this is
 * the endpoint's own ceiling rather than a screen-invented one.
 */
export const SHIFT_ASSIGNMENT_LIST_LIMIT = 200;

export interface ShiftAssignmentListQuery {
  readonly employeeId?: string | undefined;
  readonly shiftId?: string | undefined;
  readonly activeOn?: string | undefined;
  readonly includeEnded?: boolean | undefined;
}

/**
 * `GET /shift-assignments?employee_id=&shift_id=&active_on=&include_ended=`.
 *
 * `employeeId` narrows the caller's resolved scope to that one employee's own
 * `scope_type: employee` rows (02 §11 step 5) — it does **not** filter the four
 * non-employee-scoped rows (`job_title`/`department`/`branch`/`company`), which stay
 * visible to anyone whose resolved level reaches `department`
 * (`src/modules/attendance/routes.ts`'s own comment on this route). The drawer that
 * calls this says so on screen rather than implying every row it renders is this one
 * employee's own.
 */
export function shiftAssignmentsPath(query: ShiftAssignmentListQuery = {}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    employee_id: query.employeeId,
    shift_id: query.shiftId,
    active_on: query.activeOn,
    include_ended: query.includeEnded,
    limit: SHIFT_ASSIGNMENT_LIST_LIMIT,
  };
  return `${SHIFT_ASSIGNMENTS_PATH}${qs(params)}`;
}

export function shiftAssignmentPath(id: string): string {
  return `${SHIFT_ASSIGNMENTS_PATH}/${encodeURIComponent(id)}`;
}

/**
 * `POST /shift-assignments`. `openapi.yaml`'s `ShiftAssignmentCreateRequest` allows
 * five rungs (`employee_ids`, `job_title`, `department_id`, `branch_id`,
 * `company_wide`) but this screen is a roster **grid**, not a bulk-assignment tool —
 * "Gán ca" always names the one employee whose row the drawer is open on, so this
 * type only offers `employee_ids` and the two effective dates.
 */
export interface ShiftAssignmentCreateRequest {
  readonly shift_id: string;
  readonly employee_ids: readonly [string];
  readonly effective_from: string;
  readonly effective_to?: string;
}

export interface ShiftAssignmentCreateResponse {
  readonly created: readonly ShiftAssignment[];
  readonly skipped: readonly { readonly employee_id: string; readonly reason: string }[];
}

/** `DELETE /shift-assignments/{id}`. `effective_to` omitted defaults to today, server-side. */
export interface ShiftAssignmentEndRequest {
  readonly effective_to?: string;
}

/**
 * `409 WORKFORCE_VERSION_CONFLICT` with `details.reason === 'already_ended'` — the
 * one shape `endShiftAssignment` (`src/modules/attendance/service.ts`) sends for a
 * second `DELETE` on the same row. The code's generic default sentence talks about a
 * stale read ("dữ liệu đã thay đổi ở nơi khác… tải lại rồi thử lại"), which is not
 * what happened here — nothing needs reloading, the row is simply already closed —
 * so the screen substitutes its own copy for exactly this shape rather than the
 * generic one.
 */
export function isAlreadyEndedConflict(error: WorkforceApiError): boolean {
  return error.code === 'WORKFORCE_VERSION_CONFLICT' && error.details.reason === 'already_ended';
}

/* ───────────────────────────────────────────────────────────────── roster grid ── */

export const SHIFT_ROSTER_PATH = '/api/v1/shifts/roster';

/**
 * `ShiftRosterVia` of `openapi.yaml` — which rung of the precedence ladder answered
 * this day, `'default'` for the system default, or `null` only when the day is
 * `unconfigured` (no assignment and no default).
 */
export type ShiftRosterVia = ShiftScopeType | 'default' | null;

/** `null` on a day off or unconfigured — see `ShiftRosterDay.via`. */
export interface ShiftRosterShift {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly start_time: string;
  readonly end_time: string;
  readonly crosses_midnight: boolean;
}

export interface ShiftRosterDay {
  readonly date: string;
  readonly shift: ShiftRosterShift | null;
  readonly via: ShiftRosterVia;
  readonly planned_minutes: number;
  readonly warnings: readonly LabourCapWarning[];
}

export interface ShiftRosterEmployee {
  readonly employee_id: string;
  readonly full_name: string;
  readonly department_name: string | null;
  readonly days: readonly ShiftRosterDay[];
  readonly week_planned_minutes: number;
  readonly week_warnings: readonly LabourCapWarning[];
}

export interface ShiftRoster {
  readonly week_start: string;
  readonly days: readonly string[];
  readonly employees: readonly ShiftRosterEmployee[];
}

/**
 * `GET /shifts/roster?week_start=&department_id=`.
 *
 * `weekStart` must fall on the company's own `week_start_dow` — the caller (
 * `RosterScreen.tsx`) defaults it to Monday when that value is not available
 * client-side (`SessionUser` publishes no such field) and lets a `400` from this
 * endpoint carry the correction, exactly as this file's own screen documents.
 */
export function shiftRosterPath(weekStart: string, departmentId?: string): string {
  return `${SHIFT_ROSTER_PATH}${qs({ week_start: weekStart, department_id: departmentId })}`;
}
